import {
   createReservationsForUser,
   deleteReservation,
} from "../../../api/reservationsService";
import { addReservationLocal, removeReservationLocal } from "../../../store/reservationsSlice";
import { blockSlots } from "../../interaction/blockSlotAction";
import {
   applyOptimisticCreate,
   applyOptimisticRemove,
   completePendingCommand,
   resolveApiReservationId,
   rollbackOptimisticRemove,
} from "./calendarMutationSync";

/**
 * Build create payload from a reservation snapshot.
 * Does not invent sector/gearbox defaults — missing fields stay omitted.
 */
export function buildCreatePayloadFromReservation(row) {
   if (!row || typeof row !== "object") return null;
   const userId = Number(row.userId ?? row.user_id ?? row.studentId ?? 0);
   const instructorId = Number(row.instructorId ?? row.instructor_id ?? 0);
   const startTime = String(
      row.startTime ?? row.start ?? row.startDate ?? row.dateTime ?? "",
   ).trim();
   if (!userId || !instructorId || !startTime) return null;

   const sector = String(
      row.sector ?? row.reservation?.sector ?? "",
   ).trim();
   const gearbox = String(
      row.gearbox ?? row.reservation?.gearbox ?? "",
   ).trim();
   const privateMessage = String(
      row.privateMessage ?? row.reservation?.privateMessage ?? "",
   ).trim();
   const color = row.color ?? row.reservation?.color;
   const isFavorite = row.isFavorite ?? row.reservation?.isFavorite;
   const isImportant = row.isImportant ?? row.reservation?.isImportant;

   const reservation = { startTime };
   if (sector) reservation.sector = sector;
   if (gearbox) reservation.gearbox = gearbox;
   if (privateMessage) reservation.privateMessage = privateMessage;
   if (color) reservation.color = color;
   if (typeof isFavorite === "boolean") reservation.isFavorite = isFavorite;
   if (typeof isImportant === "boolean") reservation.isImportant = isImportant;

   return {
      userId,
      instructorId,
      reservations: [reservation],
   };
}

function extractCreatedReservationId(created, fallback = "") {
   if (created == null || created === true || created === false) {
      return String(fallback || "").trim();
   }
   if (typeof created === "number" || typeof created === "string") {
      return String(created).trim();
   }

   const fromList = Array.isArray(created?.reservations)
      ? created.reservations.find((row) => row?.id != null)
      : null;
   if (fromList?.id != null) {
      return String(fromList.id).trim();
   }

   if (Array.isArray(created)) {
      const row = created.find((entry) => entry?.id != null);
      if (row?.id != null) return String(row.id).trim();
   }

   const direct = created?.id ?? created?.reservationId ?? created?.reservation?.id;
   if (direct != null && direct !== "") {
      return String(direct).trim();
   }
   return String(fallback || "").trim();
}

function resolveLiveReservationId(store, patchBus, knownId) {
   const known = String(knownId || "").trim();
   if (!known) return "";
   return (
      resolveApiReservationId(store, patchBus, known) || known
   );
}

/**
 * Undo/redo entry for a successful reservation delete (cut or delete).
 * Tracks the live server id — undo recreate issues a new id that redo must use.
 */
export function buildReservationDeleteUndoEntry({
   store,
   dispatch,
   reservationId,
   eventSnapshot,
   slotKey,
   patchBus = null,
   invalidateMonth,
   onAlert,
} = {}) {
   let currentId = String(reservationId || "").trim();
   let event =
      eventSnapshot && typeof eventSnapshot === "object"
         ? { ...eventSnapshot }
         : null;
   const key = String(slotKey || "").trim();
   if (!store || !dispatch || !currentId || !event || !key) return null;

   const bus = patchBus || store.patchBus;

   return {
      undo: async () => {
         const payload = buildCreatePayloadFromReservation(event);
         if (!payload) {
            onAlert?.("error", "Nu am putut restaura rezervarea.");
            throw new Error("undo-delete-no-payload");
         }
         try {
            const created = await createReservationsForUser(payload);
            const nextId = extractCreatedReservationId(created);
            if (!nextId) {
               throw new Error("undo-delete-no-created-id");
            }

            const previousId = currentId;
            const nextEvent = { ...event, id: nextId, _optimistic: false };

            store.clearTombstone?.(previousId);
            store.clearTombstone?.(nextId);
            if (previousId && previousId !== nextId) {
               dispatch(removeReservationLocal(previousId));
            }

            rollbackOptimisticRemove(store, {
               eventId: nextId,
               event: nextEvent,
               slotKey: key,
               patchBus: bus,
            });
            dispatch(addReservationLocal(nextEvent));

            currentId = nextId;
            event = nextEvent;

            invalidateMonth?.();
            onAlert?.("success", "Rezervarea a fost restaurata.");
         } catch {
            onAlert?.("error", "Nu am putut restaura rezervarea.");
            throw new Error("undo-delete-failed");
         }
      },
      redo: async () => {
         const apiId = resolveLiveReservationId(store, bus, currentId);
         if (!apiId) {
            onAlert?.("error", "Nu am putut reface stergerea.");
            throw new Error("redo-delete-no-id");
         }
         try {
            applyOptimisticRemove(store, {
               eventId: apiId,
               patchBus: bus,
            });
            dispatch(removeReservationLocal(apiId));
            if (!apiId.startsWith("tmp-")) {
               await deleteReservation(apiId);
            }
            currentId = apiId;
            invalidateMonth?.();
            onAlert?.("success", "Stergerea a fost refacuta.");
         } catch {
            onAlert?.("error", "Nu am putut reface stergerea.");
            throw new Error("redo-delete-failed");
         }
      },
   };
}

/**
 * Undo/redo entry for a successful paste.
 * Undo deletes the live id; redo paste yields a new id for the next undo.
 */
export function buildPasteUndoEntry({
   store,
   dispatch,
   reservationId,
   patchBus = null,
   invalidateMonth,
   onAlert,
   masterDispatchCommand = null,
   slotPayload = null,
   pasteSnapshot = null,
} = {}) {
   let currentId = String(reservationId || "").trim();
   if (!store || !dispatch || !currentId) return null;
   const bus = patchBus || store.patchBus;

   return {
      undo: async () => {
         const apiId = resolveLiveReservationId(store, bus, currentId);
         if (!apiId) {
            onAlert?.("error", "Nu am putut anula lipirea.");
            throw new Error("undo-paste-no-id");
         }
         try {
            applyOptimisticRemove(store, {
               eventId: apiId,
               patchBus: bus,
               tombstone: false,
            });
            dispatch(removeReservationLocal(apiId));
            if (!apiId.startsWith("tmp-")) {
               await deleteReservation(apiId);
            }
            currentId = apiId;
            invalidateMonth?.();
            onAlert?.("success", "Lipirea a fost anulata.");
         } catch {
            onAlert?.("error", "Nu am putut anula lipirea.");
            throw new Error("undo-paste-failed");
         }
      },
      redo: async () => {
         if (
            !masterDispatchCommand ||
            typeof masterDispatchCommand.paste !== "function" ||
            !pasteSnapshot ||
            !slotPayload
         ) {
            onAlert?.("error", "Nu am putut reface lipirea.");
            throw new Error("redo-paste-unavailable");
         }
         try {
            const command = await masterDispatchCommand.paste(
               slotPayload,
               pasteSnapshot,
            );
            if (!command?.result?.ok) {
               throw new Error("redo-paste-rejected");
            }
            const nextId = String(
               command?.result?.confirmedId ||
                  command?.result?.optimisticId ||
                  "",
            ).trim();
            if (nextId) currentId = nextId;
            onAlert?.("success", "Lipirea a fost refacuta.");
         } catch {
            onAlert?.("error", "Nu am putut reface lipirea.");
            throw new Error("redo-paste-failed");
         }
      },
   };
}

/**
 * Undo/redo entry for block/unblock slot operations.
 */
export function buildBlockUndoEntry({
   interactionCtxRef,
   result,
   onAlert,
} = {}) {
   if (!result?.ok || !result?.target) return null;
   const action = String(result.action || "").trim();
   if (action !== "block" && action !== "unblock") return null;

   return {
      undo: async () => {
         const ctx = interactionCtxRef?.current;
         if (!ctx) {
            onAlert?.("error", "Nu am putut anula blocarea.");
            throw new Error("undo-block-no-ctx");
         }
         const reverseMode = action === "block" ? "unlock" : "block";
         const ok = await blockSlots(ctx, result.target, reverseMode);
         if (!ok?.ok) {
            onAlert?.("error", "Nu am putut anula blocarea.");
            throw new Error("undo-block-failed");
         }
         onAlert?.("success", "Blocarea a fost anulata.");
      },
      redo: async () => {
         const ctx = interactionCtxRef?.current;
         if (!ctx) {
            onAlert?.("error", "Nu am putut reface blocarea.");
            throw new Error("redo-block-no-ctx");
         }
         const redoMode = action === "block" ? "block" : "unlock";
         const ok = await blockSlots(ctx, result.target, redoMode);
         if (!ok?.ok) {
            onAlert?.("error", "Nu am putut reface blocarea.");
            throw new Error("redo-block-failed");
         }
         onAlert?.("success", "Blocarea a fost refacuta.");
      },
   };
}

/**
 * Undo/redo entry for a reservation created from the popup.
 * Tracks the live server id across recreate cycles.
 */
export function buildCreateUndoEntry({
   store,
   dispatch,
   reservationId,
   eventSnapshot,
   slotKey = "",
   patchBus = null,
   invalidateMonth,
   onAlert,
} = {}) {
   let currentId = String(reservationId || "").trim();
   let event =
      eventSnapshot && typeof eventSnapshot === "object"
         ? { ...eventSnapshot }
         : null;
   if (!store || !dispatch || !currentId || !event) return null;
   const bus = patchBus || store.patchBus;
   const key = String(slotKey || store.getEventSlotKey?.(currentId) || "").trim();

   return {
      undo: async () => {
         const apiId = resolveLiveReservationId(store, bus, currentId);
         if (!apiId) {
            onAlert?.("error", "Nu am putut anula crearea.");
            throw new Error("undo-create-no-id");
         }
         try {
            applyOptimisticRemove(store, {
               eventId: apiId,
               patchBus: bus,
            });
            dispatch(removeReservationLocal(apiId));
            if (!apiId.startsWith("tmp-")) {
               await deleteReservation(apiId);
            }
            currentId = apiId;
            invalidateMonth?.();
            onAlert?.("success", "Crearea a fost anulata.");
         } catch {
            onAlert?.("error", "Nu am putut anula crearea.");
            throw new Error("undo-create-failed");
         }
      },
      redo: async () => {
         const payload = buildCreatePayloadFromReservation(event);
         if (!payload) {
            onAlert?.("error", "Nu am putut reface crearea.");
            throw new Error("redo-create-no-payload");
         }
         try {
            const created = await createReservationsForUser(payload);
            const nextId = extractCreatedReservationId(created, currentId);
            if (!nextId) {
               throw new Error("redo-create-no-created-id");
            }
            const nextEvent = { ...event, id: nextId, _optimistic: false };
            if (key) {
               applyOptimisticCreate(store, {
                  event: nextEvent,
                  slotKey: key,
                  commandId: `redo-create-${nextId}`,
               });
            }
            dispatch(addReservationLocal(nextEvent));
            completePendingCommand(store, nextId);
            currentId = nextId;
            event = nextEvent;
            invalidateMonth?.();
            onAlert?.("success", "Crearea a fost refacuta.");
         } catch {
            onAlert?.("error", "Nu am putut reface crearea.");
            throw new Error("redo-create-failed");
         }
      },
   };
}
